<template>
  <div class="subject-select-container">
    <!-- 动态背景 -->
    <div class="background-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <div class="page-back" @click="goBack">
      <i class="el-icon-arrow-left"></i>
      <span>返回</span>
    </div>

    <div class="content-wrapper">
      <div class="header">
        <div class="logo-icon">🎓</div>
        <h1 class="title">选择学习科目</h1>
        <p class="subtitle">请选择您要学习的科目，开始您的学习之旅</p>
      </div>

      <div v-loading="loading" class="subjects-grid">
        <div
          v-for="subject in displaySubjects"
          :key="subject.id"
          class="subject-card"
          :class="{ 'selected': selectedSubject && selectedSubject.id === subject.id }"
          @click="selectSubject(subject)"
        >
          <div class="card-cover">
            <img
              v-if="subject.image && subject.image !== 'default'"
              :src="getImageUrl(subject.image)"
              :alt="subject.course_name"
              @error="handleImageError"
            >
            <div v-else class="default-cover">
              <i class="el-icon-reading"></i>
            </div>
          </div>
          <div class="card-content">
            <h3 class="subject-name">{{ subject.course_name }}</h3>
            <p class="subject-desc" v-if="subject.profile">{{ subject.profile }}</p>
          </div>
          <div class="card-check" v-if="selectedSubject && selectedSubject.id === subject.id">
            <i class="el-icon-check"></i>
          </div>
        </div>
      </div>

      <div v-if="!loading && displaySubjects.length === 0" class="empty-state">
        <i class="el-icon-folder-opened"></i>
        <p>暂无可选科目</p>
      </div>

      <div class="action-bar">
        <el-button
          type="primary"
          size="large"
          :disabled="!selectedSubject"
          :loading="entering"
          @click="enterSubject"
        >
          <i class="el-icon-right"></i> 进入学习
        </el-button>
      </div>

      <el-dialog
        :title="'选择科目 - ' + (selectedSubject ? selectedSubject.course_name : '')"
        :visible.sync="courseDialogVisible"
        width="520px"
        :close-on-click-modal="false"
        :append-to-body="true"
        :modal-append-to-body="true"
        custom-class="course-select-dialog"
      >
        <div v-if="currentCourseOptions.length" style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap;">
          <el-button
            v-for="course in currentCourseOptions"
            :key="course.repoId"
            type="primary"
            @click="enterCourse(course)"
          >
            {{ course.courseName }}
          </el-button>
        </div>
        <div v-else style="text-align:center;color:#999;padding:20px;">
          该类别下暂无可选科目
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { getCourseCategories, getCoursesByCategoryId } from '@/api/course'
import { fetchPaging } from '@/api/repo'

export default {
  name: 'SubjectSelect',
  data() {
    return {
      loading: false,
      entering: false,
      subjects: [],
      selectedSubject: null,
      courseDialogVisible: false,

      courseTypeList: [],
      courseTypeCoursesMap: {}
    }
  },
  computed: {
    displaySubjects() {
      const list = Array.isArray(this.courseTypeList) ? this.courseTypeList : []
      if (!list.length) {
        return []
      }

      return list.map((ct) => {
        const name = String(ct?.name || ct?.code || '').trim()
        return {
          id: ct?.id,
          course_name: name,
          image: ct?.image || 'default',
          profile: ct?.description || '',
          __courseType: ct
        }
      })
    },
    currentCourseOptions() {
      if (!this.selectedSubject) return []
      const categoryId = String(this.selectedSubject.id)
      const courses = this.courseTypeCoursesMap[categoryId] || []
      return courses.filter(c => {
        const repoId = Number(c?.repoId || 0)
        return repoId > 0
      }).map(c => ({
        courseName: String(c?.courseName || c?.course_name || c?.name || '').trim(),
        repoId: Number(c?.repoId),
        courseId: c?.id,
        categoryId: Number(categoryId)
      })).sort((a, b) => a.repoId - b.repoId)
    }
  },
  created() {
    this.loading = false
    this.loadCourseTypes()
  },
  methods: {
    async loadCourseTypes() {
      try {
        // 预先拉取题库列表，用于 course_name + categoryId -> repoId 的映射
        let repoKeyToId = new Map()
        try {
          const repoRes = await fetchPaging({})
          const payload = repoRes?.data ?? repoRes
          const listMaybe = payload?.data ?? payload
          const repos = Array.isArray(listMaybe) ? listMaybe : []
          const isDel = (r) => {
            const del = Number(r?.isDeleted != null ? r.isDeleted : r?.is_deleted)
            return del === 1
          }
          repos.forEach((r) => {
            if (!r || isDel(r)) return
            const courseId = Number(r?.courseId != null ? r.courseId : r?.course_id)
            const title = String(r?.title || '').trim().toLowerCase()
            const id = r?.id != null ? Number(r.id) : NaN
            if (!courseId || Number.isNaN(courseId)) return
            if (!title) return
            if (!id || Number.isNaN(id)) return
            repoKeyToId.set(`${courseId}|${title}`, id)
          })
        } catch (e) {
          repoKeyToId = new Map()
        }

        const res = await getCourseCategories()
        const list = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
        this.courseTypeList = list

        const coursesMap = {}
        await Promise.all(list.map(async (ct) => {
          try {
            const categoryId = ct && ct.id
            if (!categoryId) return
            const courses = await getCoursesByCategoryId(categoryId)
            const raw = Array.isArray(courses) ? courses : []
            // 只保留管理员建立的课程
            const filtered = raw.filter(c => String(c?.teacher_name || c?.teacherName || '').trim() === '管理员')

            // 使用后端返回的 repoId，如果没有则尝试从题库映射中补全
            const enriched = filtered.map((c) => {
              const existingRepoId = Number(c?.repoId || c?.repo_id || 0)
              if (existingRepoId > 0) {
                return { ...c, repoId: existingRepoId }
              }
              const courseName = String(c?.course_name || c?.courseName || c?.name || '').trim()
              const key = `${Number(categoryId)}|${courseName.toLowerCase()}`
              const rid = repoKeyToId.get(key)
              return {
                ...c,
                repoId: rid != null ? Number(rid) : 0
              }
            })

            coursesMap[String(categoryId)] = enriched
          } catch (e) {
            // ignore
          }
        }))
        this.courseTypeCoursesMap = coursesMap
      } catch (e) {
        this.courseTypeList = []
        this.courseTypeCoursesMap = {}
      }
    },
    getImageUrl(image) {
      if (!image || image === 'default') return ''
      if (image.startsWith('http')) return image
      // 拼接 MinIO 地址
      const minioBase = process.env.VUE_APP_MINIO_URL || 'http://localhost:9000'
      if (image.startsWith('/')) {
        return minioBase + image
      }
      return minioBase + '/' + image
    },
    handleImageError(e) {
      e.target.style.display = 'none'
    },
    selectSubject(subject) {
      this.selectedSubject = subject
      this.courseDialogVisible = true
    },

    enterCourse(course) {
      this.courseDialogVisible = false
      const ct = this.selectedSubject?.__courseType
      const courseTypeId = ct && ct.id != null ? String(ct.id) : ''
      const courseTypeCode = String(ct?.code || ct?.name || '').toLowerCase()
      this.$router.push({
        path: '/amc-course',
        query: {
          level: course.courseName,
          courseTypeId,
          courseTypeCode,
          repoId: String(course.repoId)
        }
      })
    },

    async enterSubject() {
      if (!this.selectedSubject) {
        this.$message.warning('请先选择一个科目')
        return
      }
      this.courseDialogVisible = true
    },
    goBack() {
      const redirect = this.$route?.query?.redirect
      if (redirect) {
        this.$router.push(String(redirect))
        return
      }
      this.$router.push('/landing')
    }
  }
}
</script>

<style lang="scss" scoped>
$bg: #0f172a;
$bg_light: #1e293b;
$primary: #3b82f6;
$primary_hover: #2563eb;
$surface: rgba(255, 255, 255, 0.08);

.subject-select-container {
  min-height: 100vh;
  background: linear-gradient(135deg, $bg 0%, $bg_light 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  box-sizing: border-box;
}

.page-back {
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 10;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
}

.page-back:hover {
  opacity: 0.85;
}

.background-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;

  .circle {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba($primary, 0.15), rgba($primary, 0.05));
    animation: float 20s ease-in-out infinite;
  }

  .circle-1 {
    width: 600px;
    height: 600px;
    top: -200px;
    right: -100px;
    animation-delay: 0s;
  }

  .circle-2 {
    width: 400px;
    height: 400px;
    bottom: -100px;
    left: -100px;
    animation-delay: -5s;
  }

  .circle-3 {
    width: 300px;
    height: 300px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation-delay: -10s;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-30px) rotate(5deg);
  }
}

.content-wrapper {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}

.header {
  text-align: center;
  margin-bottom: 40px;

  .logo-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .title {
    font-size: 32px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 12px;
  }

  .subtitle {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
  }
}

.subjects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 320px));
  gap: 24px;
  justify-content: center;
  margin-bottom: 40px;
}

.subject-card {
  background: $surface;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
    border-color: rgba($primary, 0.3);
  }

  &.selected {
    border-color: $primary;
    box-shadow: 0 0 0 4px rgba($primary, 0.2);

    .card-cover::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba($primary, 0.2);
    }
  }

  .card-cover {
    height: 160px;
    background: linear-gradient(135deg, #334155, #1e293b);
    position: relative;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .default-cover {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 48px;
        color: rgba(255, 255, 255, 0.3);
      }
    }
  }

  .card-content {
    padding: 20px;

    .subject-name {
      font-size: 18px;
      font-weight: 600;
      color: #fff;
      margin: 0 0 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .teacher-name {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.6);
      margin: 0 0 8px;

      i {
        margin-right: 4px;
      }
    }

    .subject-desc {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.5);
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-height: 1.5;
    }
  }

  .card-check {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    background: $primary;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    box-shadow: 0 4px 12px rgba($primary, 0.4);
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.5);

  i {
    font-size: 64px;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.action-bar {
  text-align: center;

  .el-button {
    min-width: 200px;
    height: 48px;
    font-size: 16px;
    border-radius: 24px;
    background: linear-gradient(135deg, $primary, $primary_hover);
    border: none;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, $primary_hover, darken($primary_hover, 5%));
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba($primary, 0.4);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    i {
      margin-right: 8px;
    }
  }
}

::v-deep .course-select-dialog {
  z-index: 3001 !important;
}

::v-deep .v-modal {
  z-index: 3000 !important;
}
</style>
